import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusPill } from "../components/StatusPill";

const INBOX_KINDS = [
  ["evidence", "Signed training record"],
  ["badge_photo", "Badge photo"], ["id_card", "ID card / driver's license"], ["ss_card", "SS card"], ["i9", "I-9"],
  ["pa_bg", "PA background check"], ["fbi_bg", "FBI / IdentoGO"], ["tb_test", "TB test"], ["drug_test", "Drug test"],
  ["cpr_first_aid", "CPR / First Aid card"], ["resume", "Resume / application"], ["credential", "Credential / certification"],
  ["auto_insurance", "Auto insurance"], ["separation_doc", "Separation document"],
  ["interview", "Interview documentation"], ["reference_check", "Reference check"],
  ["competency_exam", "Competency exam record"], ["other", "Other file"],
];

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState([]);
  const [readiness, setReadiness] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState("");
  const [evvMonth, setEvvMonth] = useState("");
  const [rules, setRules] = useState(null);

  useEffect(() => {
    api.get("/settings/compliance-rules").then((r) => setRules(r.data.rules)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api/setRules are stable; run once on mount
  }, []);

  const toggleRule = async (key) => {
    const next = rules.map((r) => (r.key === key ? { ...r, severity: r.severity === "block" ? "reminder" : "block" } : r));
    setRules(next);
    try {
      await api.put("/settings/compliance-rules", { rules: Object.fromEntries(next.map((r) => [r.key, r.severity])) });
      toast.success("Compliance rule updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", employee_id: "", start_date: "" });
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [credFor, setCredFor] = useState(null);

  const showCreds = async (i) => {
    try {
      const { data } = await api.post(`/onboarding/invites/${i.id}/reset-password`);
      setCredFor(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => { toast.error(formatApiError(e)); setUsers([]); });
    api.get("/audit-log").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/audit/staff-readiness").then((r) => setReadiness(r.data.items || [])).catch(() => {});
    api.get("/onboarding/invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const [hireSort, setHireSort] = useState("newest");
  const [search, setSearch] = useState("");
  const printFullRoster = async () => {
    try {
      const r = await api.get("/admin/reports/employee-roster.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "employee-roster.pdf";
      a.click();
      toast.success("Roster downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const printBadges = async () => {
    try {
      const r = await api.get("/admin/employees/badges.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "employee-badges.pdf";
      a.click();
      toast.success("Badge sheet downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleAccess = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.id}/access`, { enabled: !u.access_enabled });
      toast.success(`${u.name || u.email}: login access ${r.data.access_enabled ? "ON" : "OFF"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [ivFilter, setIvFilter] = useState("all");
  const [gapFilter, setGapFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("current");
  const [hireFrom, setHireFrom] = useState("");
  const [hireTo, setHireTo] = useState("");

  const [topics, setTopics] = useState([]);
  const [inboxKind, setInboxKind] = useState(() => localStorage.getItem("inboxKind") || "evidence");
  const [inboxTopic, setInboxTopic] = useState(() => localStorage.getItem("inboxTopic") || "");
  const [inboxScan, setInboxScan] = useState(null);
  const [inboxResult, setInboxResult] = useState(null);
  const [inboxBusy, setInboxBusy] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- localStorage is a stable browser global
  useEffect(() => { localStorage.setItem("inboxKind", inboxKind); }, [inboxKind]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- localStorage is a stable browser global
  useEffect(() => { localStorage.setItem("inboxTopic", inboxTopic); }, [inboxTopic]);

  useEffect(() => {
    api.get("/training/topics").then((r) => setTopics(r.data.filter((t) => t.active !== false))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api/setTopics are stable; run once on mount
  }, []);

  const scanInbox = async (files) => {
    if (!files?.length) return;
    setInboxBusy(true);
    setInboxScan(null);
    setInboxResult(null);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post("/inbox/scan", fd, { timeout: 180000 });
      data.items = data.items.map((it) => ({ ...it, kind: it.suggested_kind || "", topic_id: it.suggested_topic_id || "" }));
      setInboxScan(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInboxBusy(false);
    }
  };

  const [pool, setPool] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api/setPool are stable module imports/setters
  const loadPool = useCallback(() => api.get("/inbox/pool").then((r) => setPool(r.data)).catch(() => {}), []);
  useEffect(() => { loadPool(); }, [loadPool]);

  const dismissPoolItem = async (it) => {
    try {
      await api.delete(`/inbox/pool/${it.id}`);
      setPool((p) => p.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const attachPoolItem = async (it) => {
    const kind = it.kind || it.suggested_kind || "other";
    setInboxBusy(true);
    try {
      await api.post(`/inbox/pool/${it.id}/attach`, { uid: it.uid, kind, topic_id: kind === "evidence" ? (it.suggested_topic_id || inboxTopic) : "" });
      toast.success("Attached to employee record");
      setPool((p) => p.filter((x) => x.id !== it.id));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInboxBusy(false);
    }
  };

  const applyInbox = async () => {
    setInboxBusy(true);
    try {
      const { data } = await api.post("/inbox/apply", {
        kind: inboxKind, topic_id: inboxKind === "evidence" ? inboxTopic : "",
        items: inboxScan.items.filter((i) => i.uid && i.stored_path)
          .map((it) => ({ stored_path: it.stored_path, filename: it.filename, uid: it.uid, content_type: it.content_type,
                          kind: it.kind || inboxKind, topic_id: ((it.kind || inboxKind) === "evidence") ? (it.topic_id || inboxTopic) : "" })),
      });
      setInboxResult(data);
      setInboxScan(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInboxBusy(false);
    }
  };

  const hireDate = (u) => String((u.profile || {}).hire_date || (u.profile || {}).start_date || "").slice(0, 10);
  const rosterStatusOf = (u) => {
    const s = String((u.profile || {}).employment_status || "").trim().toLowerCase();
    if (s) return s;
    return u.active ? "active" : "inactive";
  };
  const needsStatusCheck = (u) => {
    const today = new Date();
    const last = String(u.profile?.last_visit_date || u.profile?.last_day_worked || "").slice(0, 10);
    if (last) return (today - new Date(last + "T00:00:00")) / 86400000 > 30;
    const hired = hireDate(u);
    return !!hired && (today - new Date(hired + "T00:00:00")) / 86400000 > 30;
  };
  const isFormer = (u) => !u.active || ["inactive", "terminated"].includes(String((u.profile || {}).roster_status || "").toLowerCase());

  const printRoster = () => {
    if (!users) return;
    if (!readiness.length) { toast.error("Roster status is still loading — try again in a moment"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const rows = users.filter((u) => u.active).slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((u) => {
        const r = readiness.find((x) => x.email === u.email);
        const issues = r ? r.flags.map((f) => f.label) : [];
        if (u.gaps) {
          if (u.gaps.interview) issues.push("Face-to-face interview missing");
          if (u.gaps.competency) issues.push("Initial competency exam missing");
          if (u.gaps.tb) issues.push("TB test missing");
          if (u.gaps.id) issues.push("ID card missing");
        }
        return { name: u.name || u.email, hire: hireDate(u) || "—",
                 ready: !!r && r.flag_count === 0, issues };
      });
    const ready = rows.filter((r) => r.ready);
    const notReady = rows.filter((r) => !r.ready);
    const today = new Date().toISOString().slice(0, 10);
    const section = (title, list, showIssues) => `
      <h2>${title} (${list.length})</h2>
      <table><thead><tr><th>Employee</th><th>Hire date</th>${showIssues ? "<th>Needed before rostering</th>" : "<th>Notes</th>"}</tr></thead>
      <tbody>${list.map((r) => `<tr><td>${esc(r.name)}</td><td>${esc(r.hire)}</td><td>${r.issues.length ? esc(r.issues.join("; ")) : "—"}</td></tr>`).join("") || '<tr><td colspan="3" class="none">None</td></tr>'}</tbody></table>`;
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><title>Roster status — ${today}</title><style>
      body{font-family:Georgia,serif;margin:36px;color:#1c1917}
      h1{font-size:20px;margin:0} .sub{color:#78716c;font-size:12px;margin:4px 0 20px}
      h2{font-size:14px;margin:22px 0 6px;padding-bottom:4px;border-bottom:2px solid #6a3e97}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{text-align:left;color:#78716c;text-transform:uppercase;font-size:9px;letter-spacing:.05em;padding:4px 8px;border-bottom:1px solid #d6d3d1}
      td{padding:5px 8px;border-bottom:1px solid #e7e5e4;vertical-align:top}
      .none{color:#a8a29e} @media print{body{margin:12mm}}
      </style></head><body>
      <h1>Silver Home Care — Roster status</h1>
      <p class="sub">Printed ${today} · ${ready.length} ready to roster · ${notReady.length} with items needed · active staff only</p>
      ${section("Ready to roster", ready, false)}
      ${section("Needs items before rostering", notReady, true)}
      <p class="sub" style="margin-top:24px">Rosterable = all onboarding requirements on file (see employee file for detail). This list is a snapshot — the app is the live record.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };
  const lastName = (u) => {
    const parts = String(u.name || "").trim().split(/\s+/);
    return (parts.length > 1 ? parts[parts.length - 1] : parts[0] || u.email || "").toLowerCase();
  };
  const visibleUsers = (users || [])
    .filter((u) => (staffFilter === "all" ? true : staffFilter === "former" ? isFormer(u) : !isFormer(u)))
    .filter((u) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
        || String((u.profile || {}).phone || "").toLowerCase().includes(q)
        || String((u.profile || {}).id_number || "").toLowerCase().includes(q);
    })
    .filter((u) => {
      const ivd = String((u.profile || {}).interview_date || "").slice(0, 10);
      if (ivFilter === "done") return !!ivd;
      if (ivFilter === "missing") return !ivd;
      return true;
    })
    .filter((u) => !gapFilter || Boolean(u.gaps && u.gaps[gapFilter]))
    .filter((u) => {
      const d = hireDate(u);
      if (hireFrom && (!d || d < hireFrom)) return false;
      if (hireTo && (!d || d > hireTo)) return false;
      return true;
    })
    .sort((a, b) => {
      if (hireSort === "az" || hireSort === "za") {
        const c = lastName(a).localeCompare(lastName(b)) || String(a.name || "").localeCompare(String(b.name || ""));
        return hireSort === "az" ? c : -c;
      }
      const da = hireDate(a), dbb = hireDate(b);
      if (!da && !dbb) return 0;
      if (!da) return 1;
      if (!dbb) return -1;
      return hireSort === "oldest" ? da.localeCompare(dbb) : dbb.localeCompare(da);
    });

  const add = async () => {
    if (!form.email.trim() || !form.password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`User ${form.email} created`);
      setForm({ name: "", email: "", password: "", role: "staff", employee_id: "", start_date: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelect = (u, on) => setSelected((s) => {
    const n = new Set(s);
    if (on) n.add(u.id); else n.delete(u.id);
    return n;
  });
  const bulkAction = async (action) => {
    if (!selected.size || bulkBusy) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${selected.size} account(s)? This also removes their employee-file documents and to-do items. This cannot be undone. Admin accounts and your own account are skipped.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk", { action, ids: [...selected] });
      toast.success(`${data.affected} account(s) ${action === "delete" ? "deleted" : action === "activate" ? "activated" : "deactivated"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const [invite, setInvite] = useState({ name: "", email: "", employee_id: "", start_date: "" });
  const [inviteResult, setInviteResult] = useState(null);

  const inviteNewHire = async () => {
    if (!invite.name.trim() || !invite.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    setInviteResult(null);
    try {
      const { data } = await api.post("/onboarding/invites", invite);
      setInviteResult(data);
      setInvite({ name: "", email: "", employee_id: "", start_date: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadCsv = async (kind, f) => {
    if (!f) return;
    setImportBusy(kind);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const url = kind === "employees" ? "/import/employees-csv"
        : kind === "clients" ? "/import/clients-csv"
        : kind === "restore" ? "/import/restore"
        : `/evv/upload${evvMonth ? `?month=${evvMonth}` : ""}`;
      const { data } = await api.post(url, fd, { timeout: 120000 });
      setImportResult({ kind, ...data });
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImportBusy("");
    }
  };

  return (
    <div className="max-w-6xl" data-testid="users-page">      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Team</h1>
      <p className="mt-1 text-sm text-stone-500">Staff accounts and access. Only admins can manage users or delete clients.</p>

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900"><UserPlus size={16} /> Add staff member</p>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-stone-300 bg-white" data-testid="user-name-input" />
          <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-stone-300 bg-white" data-testid="user-email-input" />
          <Input placeholder="Password (min 8 chars)" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border-stone-300 bg-white" data-testid="user-password-input" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="border-stone-300 bg-white" data-testid="user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">DCW (caregiver)</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Employee ID" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="border-stone-300 bg-white" data-testid="user-employee-id-input" />
          <Input placeholder="Start date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="border-stone-300 bg-white" data-testid="user-start-date-input" />
        </div>
        <Button onClick={add} disabled={busy} className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="add-user-button">Add User</Button>
      </div>

      <div className="mt-6 rounded-lg border border-primary/30 bg-white p-5 shadow-sm" data-testid="onboarding-invite-card">
        <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900"><UserPlus size={16} /> New hire onboarding</p>
        <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
          The handoff from your hiring funnel: when someone is marked hired, create their account here. They log in and the app walks them through orientation, the competency exam, documents, and signature — they can't be assigned to clients until it's all done.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
          <Input placeholder="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="border-stone-300 bg-white" data-testid="invite-name-input" />
          <Input placeholder="Email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="border-stone-300 bg-white" data-testid="invite-email-input" />
          <Input placeholder="Employee ID" value={invite.employee_id} onChange={(e) => setInvite({ ...invite, employee_id: e.target.value })} className="border-stone-300 bg-white" data-testid="invite-employee-id-input" />
          <Input placeholder="Start date" type="date" value={invite.start_date} onChange={(e) => setInvite({ ...invite, start_date: e.target.value })} className="border-stone-300 bg-white" data-testid="invite-start-date-input" />
        </div>
        <Button onClick={inviteNewHire} disabled={busy} className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="invite-submit-button">
          {busy ? "Creating..." : "Create onboarding account"}
        </Button>
        {inviteResult && (
          <div className="mt-3 rounded-md border border-[#B7791F]/30 bg-[#B7791F]/10 p-3" data-testid="invite-result">
            <p className="text-xs font-semibold text-[#8a5d14]">Account created — send these login details to {inviteResult.name} now (the password won't be shown again):</p>
            <p className="mt-1 font-mono text-xs text-stone-800">{inviteResult.email} — {inviteResult.temp_password}</p>
            <p className="mt-1 text-xs text-stone-500">They sign in at this app's address and follow the on-screen checklist.</p>
          </div>
        )}
      </div>

      {invites.length > 0 && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="onboarding-pending-list">
          <p className="font-heading text-sm font-semibold text-stone-900">Onboarding pipeline ({invites.filter((i) => !i.orientation_done).length} in progress)</p>
          <p className="mt-0.5 text-xs text-stone-500">New hires created here or arrived automatically from the JotForm application. They cannot be assigned to clients until orientation is signed and compliance clears.</p>
          <div className="mt-3 space-y-2">
            {invites.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-200 px-3 py-2.5" data-testid={`invite-row-${i.id}`}>
                <div>
                  <Link to={`/team/${i.id}`} className="text-sm font-medium text-stone-800 hover:text-primary hover:underline">{i.name}</Link>
                  <p className="text-xs text-stone-400">{i.email} · invited {String(i.invited_at || "").slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${i.orientation_done ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"}`} data-testid={`invite-status-${i.id}`}>
                    {i.orientation_done ? "Orientation complete" : "Orientation pending"}
                  </span>
                  {i.exam_passed
                    ? <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-medium text-[#1e8449]">Exam {i.exam_score}%</span>
                    : <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-500">Exam pending</span>}
                  <button onClick={() => showCreds(i)} className="text-xs font-medium text-primary hover:underline" data-testid={`invite-creds-${i.id}`}>Login details</button>
                </div>
              </div>
            ))}
          </div>
          {credFor && (
            <div className="mt-3 rounded-md border border-[#B7791F]/30 bg-[#B7791F]/10 p-3" data-testid="creds-result">
              <p className="text-xs font-semibold text-[#8a5d14]">Fresh login details (previous password no longer works):</p>
              <p className="mt-1 font-mono text-xs text-stone-800">{credFor.email} — {credFor.temp_password}</p>
            </div>
          )}
        </div>
      )}

      {users === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex w-fit gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm" data-testid="staff-status-filter">
            {[["current", "Current staff"], ["former", "Former employees"], ["all", "All"]].map(([key, label]) => (
              <button key={key} onClick={() => setStaffFilter(key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${staffFilter === key ? "bg-primary text-primary-foreground" : "text-stone-600 hover:bg-accent"}`}
                data-testid={`staff-filter-${key}`}>{label}</button>
            ))}
          </div>
          <button onClick={printFullRoster}
            className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-600 shadow-sm transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="staff-print-roster">Print full roster (active + everyone who has worked here)</button>
          <button onClick={printBadges}
            className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-600 shadow-sm transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="staff-print-badges">Print badge sheet (active staff)</button>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="hire-date-filter">
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Search</label>
            <Input placeholder="Name, email, phone or license #" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-52 border-stone-300 bg-white text-sm" data-testid="team-search-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Face-to-face interview</label>
            <Select value={ivFilter} onValueChange={setIvFilter}>
              <SelectTrigger className="h-9 w-40 border-stone-300 bg-white text-sm" data-testid="interview-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                <SelectItem value="done">Completed</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Missing document</label>
            <Select value={gapFilter || "all"} onValueChange={(v) => setGapFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-48 border-stone-300 bg-white text-sm" data-testid="gap-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                <SelectItem value="competency">Missing competency exam</SelectItem>
                <SelectItem value="interview">Missing interview</SelectItem>
                <SelectItem value="tb">Missing TB test</SelectItem>
                <SelectItem value="id">Missing ID card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Hired from</label>
            <Input type="date" value={hireFrom} onChange={(e) => setHireFrom(e.target.value)} className="h-9 w-40 border-stone-300 bg-white text-sm" data-testid="hire-from-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Hired to</label>
            <Input type="date" value={hireTo} onChange={(e) => setHireTo(e.target.value)} className="h-9 w-40 border-stone-300 bg-white text-sm" data-testid="hire-to-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Sort</label>
            <Select value={hireSort} onValueChange={setHireSort}>
              <SelectTrigger className="h-9 w-44 border-stone-300 bg-white text-sm" data-testid="hire-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest hires first</SelectItem>
                <SelectItem value="oldest">Oldest hires first</SelectItem>
                <SelectItem value="az">Last name A–Z</SelectItem>
                <SelectItem value="za">Last name Z–A</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(hireFrom || hireTo) && (
            <button onClick={() => { setHireFrom(""); setHireTo(""); }} className="h-9 px-2 text-xs font-medium text-stone-500 hover:text-primary" data-testid="hire-filter-clear">Clear dates</button>
          )}
          <span className="ml-auto text-xs text-stone-400" data-testid="hire-filter-count">{visibleUsers.length} of {users.length} staff</span>
          <button onClick={printRoster} className="h-9 rounded-full border border-stone-300 px-3 text-xs font-medium text-stone-600 hover:bg-stone-900 hover:text-white" data-testid="roster-print-btn">Print / PDF</button>
        </div>
        {(() => {
          const current = (users || []).filter((u) => !isFormer(u) && u.gaps);
          const counts = [["competency", "competency exam"], ["interview", "interview"], ["tb", "TB test"], ["id", "ID card"]]
            .map(([k, label]) => [k, label, current.filter((u) => u.gaps[k]).length]);
          return (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="gap-summary">
              <span className="font-medium text-stone-500">Missing among current staff:</span>
              {counts.map(([k, label, n]) => (
                <button key={k} onClick={() => setGapFilter(gapFilter === k ? "" : k)}
                  className={`rounded-full border px-2.5 py-1 font-medium transition-colors ${n === 0 ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : gapFilter === k ? "border-[#C0392B] bg-[#C0392B]/20 text-[#C0392B]" : "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B] hover:bg-[#C0392B]/20"}`}
                  data-testid={`gap-summary-${k}`}>
                  {n === 0 ? `✓ ${label}` : `${n} missing ${label}`}
                </button>
              ))}
              {gapFilter && <button onClick={() => setGapFilter("")} className="text-stone-400 hover:text-primary" data-testid="gap-filter-clear">clear filter</button>}
            </div>
          );
        })()}
        {selected.size > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-accent px-4 py-2.5" data-testid="bulk-actions-bar">
            <span className="text-sm font-medium text-stone-700" data-testid="bulk-selected-count">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => bulkAction("activate")} disabled={bulkBusy} className="border-stone-300" data-testid="bulk-activate-btn">Activate</Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction("deactivate")} disabled={bulkBusy} className="border-stone-300" data-testid="bulk-deactivate-btn">Deactivate</Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction("delete")} disabled={bulkBusy} className="border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/10" data-testid="bulk-delete-btn">Delete permanently</Button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-stone-500 hover:text-primary" data-testid="bulk-clear-btn">Clear selection</button>
          </div>
        )}
        <div className="mt-3 overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="users-table">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                <th className="w-10 px-4 py-3.5">
                  <input type="checkbox" aria-label="Select all"
                    checked={visibleUsers.length > 0 && visibleUsers.every((u) => selected.has(u.id))}
                    onChange={(e) => setSelected(e.target.checked ? new Set(visibleUsers.map((u) => u.id)) : new Set())}
                    className="h-4 w-4 accent-primary" data-testid="user-select-all" />
                </th>
                <th className="px-6 py-3.5 font-medium">Name</th>
                <th className="px-6 py-3.5 font-medium">Employee ID</th>
                <th className="px-6 py-3.5 font-medium">Status</th>
                <th className="px-6 py-3.5 font-medium">Applied</th>
                <th className="px-6 py-3.5 font-medium">Hire date</th>
                <th className="px-6 py-3.5 font-medium">First worked</th>
                <th className="px-6 py-3.5 font-medium">Last worked</th>
                <th className="px-6 py-3.5 font-medium">Phone</th>
                <th className="px-6 py-3.5 font-medium">Email</th>
                <th className="px-6 py-3.5 font-medium">EVV</th>
                <th className="px-6 py-3.5 font-medium">Compliance</th>
                <th className="px-6 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {visibleUsers.length === 0 && (
                <tr><td colSpan={10} className="px-6 py-8 text-center text-sm text-stone-400" data-testid="hire-filter-empty">No staff match the current filters.</td></tr>
              )}
              {visibleUsers.map((u) => (
                <tr key={u.id} className="border-b border-stone-100 last:border-0" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-4">
                    <input type="checkbox" aria-label={`Select ${u.name || u.email}`}
                      checked={selected.has(u.id)} onChange={(e) => toggleSelect(u, e.target.checked)}
                      className="h-4 w-4 accent-primary" data-testid={`user-select-${u.email}`} />
                  </td>
                  <td className="px-6 py-4 font-medium text-stone-900">
                    <Link to={`/team/${u.id}`} className="hover:text-primary hover:underline" data-testid={`user-profile-link-${u.email}`}>{u.name || "—"}</Link>
                    {u.pending_profile && <span className="ml-2 rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-medium text-[#8a5d14]" data-testid={`user-pending-${u.email}`}>changes pending</span>}
                    {u.stub && <span className="ml-2 rounded-full border border-primary/30 bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground" title="Inactive account created from the interview import — fill in real email/details when hired" data-testid={`user-stub-${u.email}`}>needs setup</span>}
                    {u.profile?.employee_class === "designated_dcw" && (
                      <span className="ml-2 rounded-full border border-[#6a3e97]/40 bg-[#6a3e97]/10 px-2 py-0.5 text-[10px] font-semibold text-[#6a3e97]"
                        title={`Designated DCW — hired to care for one specific known client (${u.profile?.designated_client_name || "on file"})`}
                        data-testid={`user-designated-${u.email}`}>designated DCW</span>
                    )}
                    {(u.profile?.employment_terms || []).length > 0 && (
                      <span className="ml-2 rounded-full border border-[#3E5C76]/30 bg-[#3E5C76]/10 px-2 py-0.5 text-[10px] font-semibold text-[#3E5C76]"
                        title={`Rehire — current term started ${u.profile.start_date || "?"}; ${(u.profile.employment_terms || []).length} prior term(s) archived (manage on employee page)`}
                        data-testid={`user-rehire-${u.email}`}>rehire</span>
                    )}
                    {user?.is_owner && u.email !== user.email && (
                      <button onClick={() => toggleAccess(u)}
                        className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors ${u.access_enabled ? "border-[#2E7D32]/40 bg-[#2E7D32]/10 text-[#2E7D32] hover:bg-[#2E7D32]/20" : "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B] hover:bg-[#C0392B]/20"}`}
                        title={u.access_enabled ? "Login access ON — click to disable" : "Login access OFF — click to enable"}
                        data-testid={`user-access-toggle-${u.email}`}>{u.access_enabled ? "access on" : "access off"}</button>
                    )}
                    {["inactive", "terminated"].includes((u.profile?.roster_status || "").toLowerCase()) && (
                      <span className="ml-2 rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-500"
                        title={`Roster status: ${u.profile.roster_status}${u.profile.roster_last_worked ? ` — last worked ${u.profile.roster_last_worked}` : ""}`}
                        data-testid={`user-roster-flag-${u.email}`}>roster: {u.profile.roster_status.toLowerCase()}</span>
                    )}
                    {(u.profile?.status_reconciliation || "").includes("MISMATCH") && (
                      <span className="ml-2 rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]"
                        title={u.profile.status_reconciliation}
                        data-testid={`user-status-mismatch-${u.email}`}>status mismatch</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-stone-600" data-testid={`user-empid-${u.email}`}>{(u.profile?.employee_id_alt || "").trim() || (u.profile?.employee_id || "").trim() || "—"}</td>
                  <td className="px-6 py-4 text-stone-600">
                    <StatusPill uid={u.id} status={rosterStatusOf(u)} onChanged={() => load()} testid={`user-status-pill-${u.id}`} />
                    {!(u.profile?.hhae_first_work_date || u.profile?.first_day_worked || u.profile?.last_visit_date || u.profile?.last_day_worked) && (
                      <span className="mt-1 block w-fit rounded-full border border-stone-300 bg-stone-50 px-2 py-px text-[10px] font-semibold text-stone-500"
                        title="No shifts on record in HHAeXchange — hired but never worked"
                        data-testid={`user-neverworked-${u.id}`}>never worked a day</span>
                    )}
                    {u.active && needsStatusCheck(u) && (
                      <span className="mt-1 block w-fit rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-px text-[10px] font-bold text-[#8a5d14]"
                        title="No shifts in 30+ days — confirm this person's status with the office"
                        data-testid={`user-status-check-${u.email}`}>no shifts 30d+ — confirm status</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-stone-600" data-testid={`user-applied-${u.email}`}>{fmtDate(u.profile?.application_date) || "—"}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-stone-600" data-testid={`user-hire-date-${u.email}`}>{fmtDate(hireDate(u)) || "—"}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-stone-600" data-testid={`user-first-worked-${u.email}`}>{fmtDate(u.profile?.hhae_first_work_date || u.profile?.first_day_worked) || "—"}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-stone-600" data-testid={`user-last-worked-${u.email}`}>{fmtDate(u.profile?.last_visit_date || u.profile?.last_day_worked) || "—"}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-stone-600" data-testid={`user-phone-${u.email}`}>{(u.profile?.phone || "").trim() || "—"}</td>
                  <td className="px-6 py-4 text-stone-600">{String(u.email || "").endsWith("@import.local") ? "no email on file" : u.email}</td>
                  <td className="whitespace-nowrap px-6 py-4" data-testid={`user-evv-${u.email}`}>
                    {(u.profile?.evv_utilization || "").trim() ? (
                      <>
                        <span title={`EVV utilization from the HHAeXchange caregiver report dated ${u.profile.evv_utilization_as_of || "last report"}`}
                          className={`font-semibold ${parseFloat(u.profile.evv_utilization) >= 95 ? "text-[#1e8449]" : parseFloat(u.profile.evv_utilization) >= 80 ? "text-[#8a5d14]" : "text-[#C0392B]"}`}>
                          {u.profile.evv_utilization}
                        </span>
                        <span className="block text-[10px] text-stone-400">HHA report {fmtDate(u.profile?.evv_utilization_as_of) || ""}</span>
                      </>
                    ) : <span className="text-xs text-stone-400" title="No EVV on file — this person isn't on the HHAeXchange caregiver report yet (it updates with each report you upload)">—</span>}
                  </td>
                  <td className="px-6 py-4" data-testid={`user-rosterable-${u.email}`}>
                    {(() => {
                      if (!u.active) return <span className="text-xs text-stone-400">—</span>;
                      const c = u.compliance;
                      if (!c) return <span className="text-xs text-stone-400">…</span>;
                      const items = (c.blocks || 0) + (c.warns || 0);
                      const cls = c.provisional ? "border-[#B7791F]/40 bg-[#B7791F] text-white"
                        : c.blocks > 0 ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]"
                        : items > 0 ? "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"
                        : "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]";
                      const label = c.provisional ? `PROVISIONAL — ${items} item${items === 1 ? "" : "s"}`
                        : items > 0 ? `${items} item${items === 1 ? "" : "s"} needed`
                        : "File clear";
                      return (
                        <Link to={`/team/${u.id}`} title={c.summary || ""}
                          className={`inline-block rounded border px-2 py-0.5 text-[10px] font-bold ${cls}`}
                          data-testid={`user-compliance-${u.email}`}>
                          {label}
                        </Link>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-right" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <div className="mt-10 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="compliance-rules-section">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Compliance Checklist Rules</h2>
        <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
          Decide what <span className="font-medium text-[#C0392B]">blocks</span> an employee from client work versus what only <span className="font-medium text-[#8a5d14]">reminds</span> them at login. Employees see outstanding items every login until complete; blocked employees can still log in to fix things. Terminated employees cannot log in at all. You can waive an individual item per employee on their page.
        </p>
        <div className="mt-4 space-y-1.5">
          {(rules || []).map((r) => (
            <div key={r.key} className="flex items-center justify-between rounded-md border border-stone-200 px-3 py-2" data-testid={`rule-${r.key}`}>
              <span className="text-sm text-stone-700">{r.label}</span>
              <button onClick={() => toggleRule(r.key)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${r.severity === "block" ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" : "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"}`}
                data-testid={`rule-toggle-${r.key}`}>
                {r.severity === "block" ? "BLOCKS work" : "Reminder only"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="data-export-section">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Data Export</h2>
        <p className="mt-0.5 text-xs text-stone-500">Download agency data for records, surveys, or analysis in Excel.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[["clients", "Clients CSV"], ["employees", "Employees & Training CSV"], ["care_logs", "Care Logs CSV"], ["incidents", "Incidents CSV"]].map(([key, label]) => (
            <a key={key} href={`${api.defaults.baseURL}/export/csv/${key}`} download
              className="inline-flex h-9 items-center rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
              data-testid={`export-csv-${key}`}>
              {label}
            </a>
          ))}
          <a href={`${api.defaults.baseURL}/export/backup`} download
            className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-stone-900"
            data-testid="export-backup-button">
            Full Backup (JSON)
          </a>
        </div>
        <p className="mt-2 text-xs text-[#8a5d14]">The full backup includes everything (all staff, clients, policies, trainings, orientation, notes) including login credentials — keep it private. Use it to restore into a fresh deployment: Team page → Restore Full Backup.</p>
        <p className="mt-3 text-xs text-stone-400">A complete per-client record (all forms, generated documents, signed paperwork, care logs, incidents) is exported as a ZIP from each client's page via "Export Record".</p>

        <div className="mt-5 border-t border-stone-100 pt-5" data-testid="data-import-section">
          <h3 className="font-heading text-sm font-semibold text-stone-900">Import (CSV)</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
            All imports moved to the <a href="/uploads" className="font-semibold text-primary underline" data-testid="users-uploads-link">Uploads page</a> — employee roster, client list, EVV numbers, and everything else. Temporary passwords for new logins are shown there after an employee import.
          </p>
        </div>
      </div>

      <div className="mt-10 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="evidence-inbox">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Evidence Inbox</h2>
        <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
          Drop a zip or a batch of files — signed training forms, driver's licenses, background checks, anything. Files are matched to employees by filename; review the matches, fix any that are wrong, then attach them all at once.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Select value={inboxKind} onValueChange={setInboxKind}>
            <SelectTrigger className="h-9 w-64 border-stone-300 bg-white text-sm" data-testid="inbox-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INBOX_KINDS.map(([k, l]) => (
                <SelectItem key={k} value={k}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {inboxKind === "evidence" && (
            <Select value={inboxTopic} onValueChange={setInboxTopic}>
              <SelectTrigger className="h-9 w-64 border-stone-300 bg-white text-sm" data-testid="inbox-topic-select"><SelectValue placeholder="Training topic" /></SelectTrigger>
              <SelectContent>
                {topics.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white" data-testid="inbox-scan-label">
            {inboxBusy && !inboxScan ? "Scanning..." : "Choose files or zip"}
            <input type="file" multiple accept=".zip,.pdf,.png,.jpg,.jpeg" className="hidden" data-testid="inbox-file-input"
              onChange={(e) => { scanInbox([...(e.target.files || [])]); e.target.value = ""; }} />
          </label>
        </div>
        {inboxScan && (
          <div className="mt-4" data-testid="inbox-results">
            {(inboxScan.dropped || []).length > 0 && (
              <p className="mb-2 text-xs text-[#8a5d14]" data-testid="inbox-dropped">Skipped {inboxScan.dropped.length} unsupported file{inboxScan.dropped.length === 1 ? "" : "s"}: {inboxScan.dropped.join(", ")}</p>
            )}
            {inboxScan.items.some((i) => !i.uid && !i.error) && (
              <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-accent/40 px-3 py-2" data-testid="inbox-assign-all-row">
                <span className="text-xs font-medium text-stone-600">Assign all unmatched to:</span>
                <select value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) setInboxScan((s) => ({ ...s, items: s.items.map((x) => (!x.uid && !x.error ? { ...x, uid: v } : x)) }));
                  }}
                  className="h-8 max-w-64 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700"
                  data-testid="inbox-assign-all">
                  <option value="">— choose employee —</option>
                  {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
                <span className="text-xs text-stone-400">handy when a batch of scans all belongs to one person</span>
              </div>
            )}
            <div className="max-h-96 space-y-1.5 overflow-y-auto">
              {inboxScan.items.map((it, idx) => (
                <div key={it.stored_path || idx} className="flex flex-wrap items-center gap-2 rounded-md border border-stone-200 px-3 py-2 text-sm" data-testid={`inbox-row-${idx}`}>
                  <span className="min-w-0 flex-1 truncate text-stone-700">{it.filename}</span>
                  {it.stored_path && (
                    <a href={`${api.defaults.baseURL}/inbox/file?path=${encodeURIComponent(it.stored_path)}`} target="_blank" rel="noreferrer"
                      className="shrink-0 text-stone-400 hover:text-primary" title="Preview file" data-testid={`inbox-preview-${idx}`}>
                      <Eye size={15} />
                    </a>
                  )}
                  {it.error ? (
                    <span className="text-xs text-[#C0392B]">{it.error}</span>
                  ) : (
                    <>
                      {it.parsed_name && <span className="text-xs italic text-stone-400">{it.parsed_name}</span>}
                      <select value={it.uid}
                        onChange={(e) => setInboxScan((s) => ({ ...s, items: s.items.map((x, i) => (i === idx ? { ...x, uid: e.target.value } : x)) }))}
                        className={`h-8 max-w-56 rounded-md border px-1.5 text-xs ${it.uid ? "border-stone-300 bg-white text-stone-700" : "border-[#C0392B]/40 bg-[#C0392B]/5 text-[#C0392B]"}`}
                        data-testid={`inbox-employee-${idx}`}>
                        <option value="">— no match —</option>
                        {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                      </select>
                      <select value={it.kind || ""}
                        onChange={(e) => setInboxScan((s) => ({ ...s, items: s.items.map((x, i) => (i === idx ? { ...x, kind: e.target.value } : x)) }))}
                        className="h-8 max-w-44 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700"
                        data-testid={`inbox-dest-${idx}`}>
                        <option value="">Batch: {INBOX_KINDS.find(([k]) => k === inboxKind)?.[1]}</option>
                        {INBOX_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </>
                  )}
                </div>
              ))}
            </div>
            <Button onClick={applyInbox} disabled={inboxBusy || inboxScan.items.some((i) => i.uid && (i.kind || inboxKind) === "evidence" && !(i.topic_id || inboxTopic))}
              className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="inbox-apply-button">
              {inboxBusy ? "Attaching..." : `Attach ${inboxScan.items.filter((i) => i.uid).length} matched files`}
            </Button>
          </div>
        )}
        {inboxResult && (
          <p className="mt-3 rounded-md border border-stone-200 bg-accent/40 p-3 text-sm font-medium text-stone-800" data-testid="inbox-result">
            {inboxResult.attached} attached · {inboxResult.skipped} skipped (no employee / duplicate){(inboxResult.errors || []).length > 0 ? ` · errors: ${inboxResult.errors.join(", ")}` : ""}
          </p>
        )}
      </div>

      {pool.length > 0 && (
        <div className="mt-6 rounded-lg border border-[#B7791F]/40 bg-white p-6 shadow-sm" data-testid="unmatched-pool">
          <h2 className="font-heading text-lg font-semibold text-stone-900">Unmatched files ({pool.length})</h2>
          <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
            Files that arrived without a clear employee match. Preview with the eye icon, pick the person and destination, attach — two clicks each.
          </p>
          <div className="mt-4 max-h-96 space-y-1.5 overflow-y-auto">
            {pool.map((it) => (
              <div key={it.id} className="flex flex-wrap items-center gap-2 rounded-md border border-stone-200 px-3 py-2 text-sm" data-testid={`pool-row-${it.id}`}>
                <a href={`${api.defaults.baseURL}/inbox/file?path=${encodeURIComponent(it.stored_path)}`} target="_blank" rel="noreferrer"
                  className="shrink-0 text-stone-400 hover:text-primary" title="Preview file" data-testid={`pool-preview-${it.id}`}>
                  <Eye size={15} />
                </a>
                <span className="min-w-0 flex-1 truncate text-stone-700">
                  {it.filename}
                  {it.parsed_name ? <span className="ml-1 text-xs italic text-stone-400">{it.parsed_name}</span> : null}
                </span>
                <select value={it.uid || ""}
                  onChange={(e) => setPool((p) => p.map((x) => (x.id === it.id ? { ...x, uid: e.target.value } : x)))}
                  className="h-8 max-w-48 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700" data-testid={`pool-employee-${it.id}`}>
                  <option value="">— employee —</option>
                  {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
                <select value={it.kind || it.suggested_kind || "other"}
                  onChange={(e) => setPool((p) => p.map((x) => (x.id === it.id ? { ...x, kind: e.target.value } : x)))}
                  className="h-8 max-w-44 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-700" data-testid={`pool-kind-${it.id}`}>
                  {INBOX_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <Button onClick={() => attachPoolItem(it)} disabled={!it.uid || inboxBusy}
                  className="h-8 bg-primary px-3 text-xs text-primary-foreground hover:bg-stone-900" data-testid={`pool-attach-${it.id}`}>
                  Attach
                </Button>
                <button onClick={() => dismissPoolItem(it)} disabled={inboxBusy}
                  className="h-8 px-2 text-xs font-medium text-stone-400 hover:text-[#C0392B]" data-testid={`pool-dismiss-${it.id}`}>
                  Dismiss
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="mt-10 font-heading text-lg font-semibold text-stone-900">Recent Activity (Audit Log)</h2>
      <div className="mt-3 rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="audit-log">
        {audit.length === 0 ? (
          <p className="p-6 text-sm text-stone-500">No activity recorded yet.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {audit.slice(0, 50).map((e, i) => (
              <div key={e.id || `${e.at}-${e.actor}-${e.action}`} className="flex items-center gap-4 px-6 py-3 text-sm">
                <span className="w-40 shrink-0 text-xs text-stone-400">{String(e.at).slice(0, 16).replace("T", " ")}</span>
                <span className="shrink-0 font-medium text-stone-700">{e.actor}</span>
                <span className="text-stone-600">{e.action}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
